import { X, Phone, MessageCircle, Star, Bike, Car, Lock } from "lucide-react";
import { whatsappLink, whatsappMessageFor, normalisePhone } from "@/lib/contactActions";

/**
 * Full driver profile bottom sheet — opened when the customer taps the
 * driver card on the tracking screen. Big Call + WhatsApp + Message CTAs so
 * the customer can coordinate the final drop-off without hunting for buttons.
 */
export default function DriverProfileDrawer({ driver, canChat, onClose, onMessage, order }) {
  const {
    name = "Your driver",
    photo,
    vehicle = "Scooter",
    vehicleDetail = "",
    rating = 4.9,
    phone,
  } = driver || {};

  const cleanedPhone = normalisePhone(phone);
  const call = () => {
    window.location.href = cleanedPhone ? `tel:${cleanedPhone}` : `tel:+14735550000`;
  };
  const waHref = whatsappLink(
    cleanedPhone || "+14735550000",
    whatsappMessageFor("customer_to_driver", order || {}),
  );

  const VehicleIcon = vehicle === "Car" ? Car : Bike;

  return (
    <div
      className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center p-3 sm:p-6 fade-up"
      data-testid="driver-profile-drawer"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl overflow-hidden">
        {/* Cover art strip */}
        <div className="relative h-24 bg-gradient-to-r from-eatup-green to-eatup-green-dark">
          <button
            onClick={onClose}
            data-testid="driver-profile-close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/20 backdrop-blur text-white hover:bg-white/30 flex items-center justify-center transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Avatar + identity */}
        <div className="-mt-14 px-6 pb-6">
          <div className="flex flex-col items-center">
            {photo ? (
              <img
                src={photo}
                alt={name}
                className="w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-lg"
                data-testid="profile-avatar"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-eatup-charcoal text-white ring-4 ring-white shadow-lg flex items-center justify-center font-display font-black text-3xl">
                {name[0]?.toUpperCase()}
              </div>
            )}
            <div className="mt-3 font-display font-black text-2xl text-eatup-charcoal" data-testid="profile-name">
              {name}
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-neutral-500">
              <span className="inline-flex items-center gap-1 text-eatup-yellow font-bold">
                <Star className="w-4 h-4 fill-eatup-yellow" /> {rating.toFixed(1)}
              </span>
              <span>·</span>
              <span
                data-testid="profile-vehicle"
                className="inline-flex items-center gap-1 rounded-full bg-eatup-green/10 text-eatup-green border border-eatup-green/30 px-2.5 py-0.5 font-black uppercase tracking-widest text-[11px]"
              >
                <VehicleIcon className="w-3 h-3" /> {vehicle}
              </span>
            </div>
            {vehicleDetail && (
              <div className="mt-1 text-xs text-neutral-500">{vehicleDetail}</div>
            )}
          </div>

          {/* Big action buttons — thumb-reach Call · WhatsApp · Message */}
          <div className="mt-6 grid grid-cols-3 gap-2.5">
            <button
              onClick={call}
              data-testid="profile-call-btn"
              className="rounded-2xl bg-eatup-green hover:bg-eatup-green-dark text-white py-4 flex flex-col items-center gap-1 shadow-lg active:scale-[0.98] transition"
            >
              <Phone className="w-5 h-5" />
              <span className="font-black uppercase tracking-widest text-[11px]">Call</span>
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              data-testid="profile-whatsapp-btn"
              aria-label="Chat on WhatsApp"
              className="rounded-2xl bg-[#25D366] hover:bg-[#1ebe57] text-white py-4 flex flex-col items-center gap-1 shadow-lg active:scale-[0.98] transition"
            >
              <svg viewBox="0 0 32 32" className="w-5 h-5 fill-current" aria-hidden="true">
                <path d="M19.11 17.87c-.31-.16-1.83-.9-2.11-1s-.49-.16-.7.16-.8 1-.98 1.22c-.18.21-.36.24-.67.08-.31-.16-1.3-.48-2.48-1.53-.92-.82-1.54-1.83-1.72-2.14-.18-.31-.02-.48.13-.63.14-.14.31-.36.47-.54.16-.18.21-.31.31-.52.1-.21.05-.4-.03-.55-.08-.16-.7-1.69-.96-2.32-.25-.61-.5-.53-.7-.54h-.6c-.21 0-.55.08-.83.4-.28.31-1.09 1.07-1.09 2.6s1.12 3.02 1.27 3.23c.16.21 2.2 3.36 5.34 4.72.75.32 1.33.52 1.78.66.75.24 1.43.21 1.97.13.6-.09 1.83-.75 2.09-1.47.26-.72.26-1.34.18-1.47-.08-.13-.28-.21-.6-.37zM16 3C8.82 3 3 8.82 3 16c0 2.29.6 4.44 1.65 6.31L3 29l6.86-1.62A12.94 12.94 0 0 0 16 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.63c-2.02 0-3.9-.6-5.47-1.62l-.39-.24-3.79.89.9-3.7-.26-.4A10.6 10.6 0 0 1 5.37 16C5.37 10.14 10.14 5.37 16 5.37S26.63 10.14 26.63 16 21.86 26.63 16 26.63z"/>
              </svg>
              <span className="font-black uppercase tracking-widest text-[11px]">WhatsApp</span>
            </a>
            <button
              onClick={onMessage}
              disabled={!canChat}
              data-testid="profile-message-btn"
              className={`rounded-2xl py-4 flex flex-col items-center gap-1 shadow-lg transition active:scale-[0.98] ${
                canChat
                  ? "bg-eatup-charcoal hover:bg-black text-white"
                  : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
              }`}
            >
              {canChat ? <MessageCircle className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
              <span className="font-black uppercase tracking-widest text-[11px]">Message</span>
            </button>
          </div>

          {!canChat && (
            <div className="mt-3 text-xs text-center text-neutral-500 font-semibold">
              Message chat opens once your driver is on the way.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
